import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { AlertCircle, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import './RejectionNotices.scss';

interface UserNotification {
  id: number;
  type: string;
  title: string;
  message: string | null;
  created_at: string;
}

/**
 * Shows a one-time popup with admin rejection notices (CV / job) the next time
 * the user opens the site. Dismissing marks the notices seen so they don't
 * reappear. Mounted globally; only fetches for authenticated users.
 */
const RejectionNotices: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [notices, setNotices] = useState<UserNotification[]>([]);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setNotices([]);
      return;
    }
    let cancelled = false;
    api
      .get('/notifications/me')
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data;
        if (Array.isArray(data) && data.length > 0) setNotices(data);
      })
      .catch(() => {
        /* silent — a notification fetch failure shouldn't disrupt the app */
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const dismiss = useCallback(async () => {
    if (dismissing) return;
    setDismissing(true);
    const ids = notices.map((n) => n.id);
    try {
      await api.post('/notifications/me/seen', { ids });
    } catch {
      /* even if marking fails, close locally */
    } finally {
      setNotices([]);
      setDismissing(false);
    }
  }, [dismissing, notices]);

  if (notices.length === 0) return null;

  return ReactDOM.createPortal(
    <div className="rejection-notices-overlay" onClick={dismiss}>
      <div className="rejection-notices-modal" onClick={(e) => e.stopPropagation()}>
        <button className="rejection-notices-close" onClick={dismiss} aria-label="Uždaryti">
          <X size={20} />
        </button>

        <div className="rejection-notices-header">
          <AlertCircle size={28} />
          <h2>{notices.length > 1 ? 'Pranešimai' : 'Pranešimas'}</h2>
        </div>

        <div className="rejection-notices-body">
          {notices.map((n) => (
            <div key={n.id} className="rejection-notice">
              <div className="rejection-notice__title">{n.title}</div>
              {n.message ? (
                <div className="rejection-notice__reason">
                  <span className="rejection-notice__reason-label">Priežastis:</span> {n.message}
                </div>
              ) : (
                <div className="rejection-notice__reason rejection-notice__reason--muted">
                  Priežastis nenurodyta.
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="rejection-notices-footer">
          <button className="btn-primary" onClick={dismiss} disabled={dismissing}>
            Supratau
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RejectionNotices;
